/**
 * Task #3820 — Lavori PA con stato, piano e bozze.
 *
 * Contratto condiviso BE↔FE del modello "lavoro": piano multi-step proposto
 * dal PA prima di eseguire azioni irreversibili verso l'esterno, con
 * approvazione esplicita, avanzamento passo-passo e output impegnativi come
 * bozza-artefatto da confermare.
 *
 * Macchina a stati (la SoT delle transizioni valide vive qui, il claim
 * guardato vive nel backend `pa-job-state.ts`):
 *   proposed → approved | rejected | cancelled
 *   approved → running | cancelled
 *   running → awaiting_review | completed | failed | cancelled
 *   awaiting_review → running | completed | failed | cancelled
 *   completed / rejected / failed / cancelled = terminali
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Stati del lavoro
// ---------------------------------------------------------------------------
export const PA_JOB_STATUSES = [
  'proposed',
  'approved',
  'rejected',
  'running',
  'awaiting_review',
  'completed',
  'failed',
  'cancelled',
] as const;

export const PaJobStatusSchema = z.enum(PA_JOB_STATUSES);
export type PaJobStatus = z.infer<typeof PaJobStatusSchema>;

/** Transizioni valide (SoT condivisa: il backend le applica con claim guardato). */
export const PA_JOB_ALLOWED_TRANSITIONS: Record<PaJobStatus, readonly PaJobStatus[]> = {
  proposed: ['approved', 'rejected', 'cancelled'],
  approved: ['running', 'cancelled'],
  running: ['awaiting_review', 'completed', 'failed', 'cancelled'],
  awaiting_review: ['running', 'completed', 'failed', 'cancelled'],
  completed: [],
  rejected: [],
  failed: [],
  cancelled: [],
};

/** Stati "attivi" (lavoro in corso o in attesa di input): guida sidebar + gate. */
export const PA_JOB_ACTIVE_STATUSES: readonly PaJobStatus[] = [
  'proposed', 'approved', 'running', 'awaiting_review',
];

// ---------------------------------------------------------------------------
// Passi del piano
// ---------------------------------------------------------------------------
export const PaJobStepStatusSchema = z.enum(['pending', 'running', 'completed', 'failed', 'skipped']);
export type PaJobStepStatus = z.infer<typeof PaJobStepStatusSchema>;

export const PaJobStepSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1).max(300),
  /** Tool coinvolto nel passo (null = passo descrittivo senza tool diretto). */
  toolName: z.string().max(200).nullable(),
  /** Correlazione con la tool call del loop (per l'avanzamento live). */
  toolCallId: z.string().max(255).nullable(),
  /** True se il passo ha innescato il criterio conservativo (azione irreversibile). */
  requiresReview: z.boolean(),
  status: PaJobStepStatusSchema,
  detail: z.string().max(2000).nullable(),
});
export type PaJobStep = z.infer<typeof PaJobStepSchema>;

export const PaJobPlanSchema = z.array(PaJobStepSchema);

// ---------------------------------------------------------------------------
// Lavoro persistito (DB row → API response)
// ---------------------------------------------------------------------------
export const PaJobSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  assistantId: z.string().uuid(),
  userId: z.string().uuid(),
  title: z.string(),
  plan: PaJobPlanSchema,
  status: PaJobStatusSchema,
  statusVersion: z.number().int().nonnegative(),
  triggerTools: z.array(z.string()),
  decisionNote: z.string().nullable(),
  resultSummary: z.string().nullable(),
  error: z.string().nullable(),
  proposedAt: z.string(),
  decidedAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PaJob = z.infer<typeof PaJobSchema>;

// ---------------------------------------------------------------------------
// API request schemas
// ---------------------------------------------------------------------------
export const PaJobDecideRequestSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  /** "Modifica a parole": nota che accompagna la decisione e rientra nel loop. */
  note: z.string().max(2000).optional(),
  /** Lock ottimistico: rifiuta decisioni prese su uno stato stale. */
  expectedVersion: z.number().int().nonnegative(),
});
export type PaJobDecideRequest = z.infer<typeof PaJobDecideRequestSchema>;

export const PaJobCancelRequestSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type PaJobCancelRequest = z.infer<typeof PaJobCancelRequestSchema>;
